import { useEffect, useId, useRef, useState } from 'react';
import { Field } from '@src/components/Field/Field';
import { Button } from '@src/components/Button/Button';
import { Checkbox } from '@src/components/Checkbox/Checkbox';
import { CloseIcon } from '@src/components/icons/CloseIcon';
import { FilterIcon } from '@src/components/icons/FilterIcon';
import { SearchIcon } from '@src/components/icons/SearchIcon';
import './ParticipantSearch.css';

interface Props {
  query: string;
  onQueryChange: (value: string) => void;
  selected: string[];
  onSelectedChange: (values: string[]) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  filtersLabel: string;
  closeLabel: string;
  removeLabel: (label: string) => string;
}

export function ParticipantSearch({ query, onQueryChange, selected, onSelectedChange, options, placeholder, filtersLabel, closeLabel, removeLabel }: Props) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const searchToggle = useRef<HTMLButtonElement>(null);
  const restoreFocus = useRef(false);
  useEffect(() => {
    if (!expanded && restoreFocus.current) {
      searchToggle.current?.focus();
      restoreFocus.current = false;
    }
  }, [expanded]);
  function closeSearch() {
    restoreFocus.current = true;
    setOpen(false);
    setExpanded(false);
    onQueryChange('');
    onSelectedChange([]);
  }
  const host = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    function outside(event: PointerEvent) {
      if (!host.current?.contains(event.target as Node)) setOpen(false);
    }
    function escape(event: KeyboardEvent) {
      if (event.key === 'Escape') { setOpen(false); toggle.current?.focus(); }
    }
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  const change = (value: string, checked: boolean) => onSelectedChange(checked ? [...selected, value] : selected.filter((item) => item !== value));
  if (!expanded) return <Button ref={searchToggle} variant="ghost" icon={<SearchIcon />} aria-label={placeholder} aria-expanded={false} onClick={() => setExpanded(true)} testId="game-participants-search-toggle" />;
  return (
    <div className="participant-search" ref={host}>
      <Field controlId={id} className="participant-search__field">
        <SearchIcon />
        <div className="participant-search__tokens">
          {options.filter((option) => selected.includes(option.value)).map((option) => (
            <button key={option.value} type="button" className="participant-search__tag" onClick={() => change(option.value, false)} aria-label={removeLabel(option.label)}>
              {option.label}<CloseIcon />
            </button>
          ))}
          <input autoFocus id={id} type="search" className="input__control" value={query} onChange={(event) => onQueryChange(event.target.value)} onKeyDown={(event) => { if (event.key === 'Escape' && !open) { event.preventDefault(); closeSearch(); } }} placeholder={placeholder} aria-label={placeholder} data-testid="game-participants-search-control" />
        </div>
        <Button ref={toggle} variant="ghost" icon={<FilterIcon />} aria-label={filtersLabel} aria-expanded={open} aria-controls={open ? `${id}-filters` : undefined} onClick={() => setOpen(!open)} testId="game-participants-filter" />
        <Button variant="ghost" icon={<CloseIcon />} aria-label={closeLabel} onClick={closeSearch} testId="game-participants-search-close" />
      </Field>
      {open && <div id={`${id}-filters`} className="participant-search__options" role="group" aria-label={filtersLabel}>
        {options.map((option) => <Checkbox key={option.value} checked={selected.includes(option.value)} onCheckedChange={(checked) => change(option.value, checked)} label={option.label} />)}
      </div>}
    </div>
  );
}
